import {Layout} from '../../src/Layout';
export default Layout;

import {DateField} from '@react-spectrum/s2';
import docs from 'docs:@react-spectrum/s2';

export const tags = ['calendar'];
export const description = 'Allows a user to enter and edit date values using a keyboard.';

# DateField

<PageDescription>{docs.exports.DateField.description}</PageDescription>

<VisualExample
  component={DateField}
  docs={docs.exports.DateField}
  props={['label', 'granularity', 'size', 'labelPosition', 'description', 'contextualHelp', 'isDisabled']}
  initialProps={{label: 'Birth date'}}
  importSource="@react-spectrum/s2/DateField"
  type="s2" />

## Value

Use the `value` or `defaultValue` prop to set the date value, using objects in the [@internationalized/date](react-aria:internationalized/date/) package. This library supports parsing date strings in multiple formats, manipulation across international calendar systems, time zones, etc.

```tsx render
"use client";
import {parseDate, getLocalTimeZone, CalendarDate} from '@internationalized/date';
import {useDateFormatter} from 'react-aria/useDateFormatter';
import {DateField} from '@react-spectrum/s2/DateField';
import {useState} from 'react';

function Example() {
  let [date, setDate] = useState<CalendarDate | null>(parseDate('2020-02-03'));
  let formatter = useDateFormatter({ dateStyle: 'full' });

  return (
    <>
      <DateField
        ///- begin highlight -///
        value={date}
        onChange={setDate} />
      {/*- end highlight -*/}
      <p>Selected date: {date ? formatter.format(date.toDate(getLocalTimeZone())) : '--'}</p>
    </>
  );
}
```

### Format options

The date format is automatically determined based on the user's locale. `DateField` supports several props to control how values are displayed.

```tsx render docs={docs.exports.DateField} links={docs.links} props={['granularity', 'hourCycle', 'hideTimeZone', 'shouldForceLeadingZeros']} initialProps={{label: 'Date'}}
"use client";
import {parseZonedDateTime} from '@internationalized/date';
import {DateField} from '@react-spectrum/s2/DateField';

<DateField
  /* PROPS */
  defaultValue={parseZonedDateTime('2025-02-03T08:45:00[America/Los_Angeles]')} />
```

### International calendars

By default, `DateField` displays the value using the calendar system for the user's locale. Use `<Provider>` to override the calendar system by setting the [Unicode calendar locale extension](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/calendar#adding_a_calendar_in_the_locale_string). The `onChange` event always receives a date in the same calendar as the `value` or `defaultValue` (Gregorian if no value is provided), regardless of the displayed locale.

```tsx render wide docs={docs.exports.Provider} links={docs.links} props={['locale']} initialProps={{locale: 'hi-IN-u-ca-indian'}}
"use client";
import {Provider} from '@react-spectrum/s2/Provider';
import {DateField} from '@react-spectrum/s2/DateField';
import {parseZonedDateTime} from '@internationalized/date';

<Provider/* PROPS */>
  <DateField defaultValue={parseZonedDateTime('2025-02-03T08:45:00[America/Los_Angeles]')} />
</Provider>
```

## Forms

Use the `name` prop to submit the selected date to the server as an [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) string. Set the `isRequired`, `minValue`, or `maxValue` props to validate the value, or implement custom client or server-side validation. See the [Forms](forms) guide to learn more.

```tsx render docs={docs.exports.DateField} links={docs.links} props={['isRequired', 'necessityIndicator']} initialProps={{isRequired: true}} type="s2" wide
"use client";
import {today, getLocalTimeZone} from '@internationalized/date';
import {DateField} from '@react-spectrum/s2/DateField';
import {Form} from '@react-spectrum/s2/Form';
import {Button} from '@react-spectrum/s2/Button';

function Example(props) {
  return (
    <Form>
      <DateField
        {...props}
        label="Appointment date"
        ///- begin highlight -///
        name="date"
        /* PROPS */
        minValue={today(getLocalTimeZone())}
        ///- end highlight -///
      />
      <Button type="submit">Submit</Button>
    </Form>
  );
}
```

## API

<PropTable component={docs.exports.DateField} links={docs.links} />
